// src/components/RBAC/RBACView.jsx
import React, { useState, useEffect } from 'react';
import { developersApi, orgsApi } from '../../services/api';
import { ShieldCheck, Check, X, Save, Info } from 'lucide-react';

const ROLES = {
  admin:     { color: '#f87171', bg: 'rgba(239,68,68,.15)',  label: 'Admin',     desc: 'Full platform access — can manage everything including RBAC settings' },
  developer: { color: '#60a5fa', bg: 'rgba(59,130,246,.15)', label: 'Developer', desc: 'Can manage applications and view/manage logs and alerts' },
  ops:       { color: '#fbbf24', bg: 'rgba(245,158,11,.15)', label: 'Ops',       desc: 'Can manage infrastructure, sources, and respond to alerts' },
  viewer:    { color: '#94a3b8', bg: 'rgba(100,116,139,.2)', label: 'Viewer',    desc: 'Read-only access to logs and alert feed' },
};

const PERMISSIONS = [
  { key: 'canViewLogs',         label: 'View Logs',         icon: '📋', desc: 'Access to the live log stream and log history' },
  { key: 'canManageAlerts',     label: 'Manage Alerts',     icon: '🔔', desc: 'Acknowledge and resolve alerts' },
  { key: 'canManageApps',       label: 'Manage Apps',       icon: '⚡', desc: 'Create, edit, and delete applications' },
  { key: 'canManageProjects',   label: 'Manage Projects',   icon: '📁', desc: 'Create, edit, and archive projects' },
  { key: 'canManageOrg',        label: 'Manage Org',        icon: '🏢', desc: 'Edit organization settings and plan' },
  { key: 'canManageDevelopers', label: 'Manage Developers', icon: '👥', desc: 'Invite, edit, and remove team members' },
  { key: 'canManageRBAC',       label: 'Manage RBAC',       icon: '🛡️', desc: 'Configure roles and permissions for all developers' },
];

const ROLE_DEFAULTS = {
  admin:     { canViewLogs: true,  canManageAlerts: true,  canManageApps: true,  canManageProjects: true,  canManageOrg: true,  canManageDevelopers: true,  canManageRBAC: true },
  developer: { canViewLogs: true,  canManageAlerts: true,  canManageApps: true,  canManageProjects: false, canManageOrg: false, canManageDevelopers: false, canManageRBAC: false },
  ops:       { canViewLogs: true,  canManageAlerts: true,  canManageApps: false, canManageProjects: false, canManageOrg: false, canManageDevelopers: false, canManageRBAC: false },
  viewer:    { canViewLogs: true,  canManageAlerts: false, canManageApps: false, canManageProjects: false, canManageOrg: false, canManageDevelopers: false, canManageRBAC: false },
};

function initials(name) {
  return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

export default function RBACView() {
  const [devs, setDevs]       = useState([]);
  const [orgs, setOrgs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [perms, setPerms]     = useState({});
  const [dirty, setDirty]     = useState({});
  const [saving, setSaving]   = useState(null);
  const [saved, setSaved]     = useState(null);
  const [orgFilter, setOrgFilter] = useState('ALL');
  const [activeTab, setActiveTab] = useState('matrix');

  async function load() {
    setLoading(true);
    try {
      const [devList, orgList] = await Promise.all([developersApi.list(), orgsApi.list()]);
      setDevs(devList);
      setOrgs(orgList);
      const p = {};
      devList.forEach(d => { p[d._id] = { ...ROLE_DEFAULTS[d.role], ...(d.permissions || {}) }; });
      setPerms(p);
    } catch {}
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  function toggle(devId, pKey) {
    setPerms(prev => ({ ...prev, [devId]: { ...prev[devId], [pKey]: !prev[devId]?.[pKey] } }));
    setDirty(prev => ({ ...prev, [devId]: true }));
  }

  async function savePerms(devId) {
    setSaving(devId);
    try {
      await developersApi.updatePerms(devId, perms[devId]);
      setDirty(prev => ({ ...prev, [devId]: false }));
      setSaved(devId);
      setTimeout(() => setSaved(null), 2000);
    } finally { setSaving(null); }
  }

  function applyRole(devId, role) {
    setPerms(prev => ({ ...prev, [devId]: { ...ROLE_DEFAULTS[role] } }));
    setDirty(prev => ({ ...prev, [devId]: true }));
  }

  const displayed = devs.filter(d => orgFilter === 'ALL' || String(d.orgId) === orgFilter);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-6 py-4 shrink-0 flex items-center gap-4"
        style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
        <ShieldCheck size={20} style={{ color: '#60a5fa' }} />
        <div>
          <h1 className="text-base font-semibold">Role-Based Access Control</h1>
          <p className="text-xs mt-0.5" style={{ color: 'var(--text-3)' }}>Manage permissions for each developer across the organization</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <select value={orgFilter} onChange={e => setOrgFilter(e.target.value)}
            style={{ padding: '5px 8px', borderRadius: 6, fontSize: 12, background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none', cursor: 'pointer' }}>
            <option value="ALL">All Organizations</option>
            {orgs.map(o => <option key={o._id} value={String(o._id)}>{o.name}</option>)}
          </select>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-6 flex gap-4 shrink-0" style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
        {[{ id: 'matrix', label: 'Permission Matrix' }, { id: 'roles', label: 'Role Definitions' }].map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            style={{
              padding: '10px 0', fontSize: 13, fontWeight: activeTab === t.id ? 600 : 400,
              color: activeTab === t.id ? '#60a5fa' : 'var(--text-3)',
              borderBottom: activeTab === t.id ? '2px solid #60a5fa' : '2px solid transparent',
              background: 'none', border: 'none', cursor: 'pointer', marginBottom: -1,
            }}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto">
        {activeTab === 'roles' ? (
          <div className="p-6">
            <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
              {Object.entries(ROLES).map(([role, s]) => (
                <div key={role} className="rounded-xl p-5" style={{ background: 'var(--bg-secondary)', border: `1px solid ${s.color}30` }}>
                  <div className="flex items-center gap-2 mb-2">
                    <span style={{ padding: '3px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700, background: s.bg, color: s.color }}>{s.label}</span>
                  </div>
                  <p className="text-sm mb-4" style={{ color: 'var(--text-2)' }}>{s.desc}</p>
                  <div className="space-y-1.5">
                    {PERMISSIONS.map(p => {
                      const has = ROLE_DEFAULTS[role][p.key];
                      return (
                        <div key={p.key} className="flex items-center gap-2 text-xs" style={{ color: has ? 'var(--text)' : 'var(--text-3)' }}>
                          <span style={{ color: has ? '#22c55e' : '#64748b', lineHeight: 0 }}>
                            {has ? <Check size={12} /> : <X size={12} />}
                          </span>
                          <span>{p.icon} {p.label}</span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--border)' }}>
                    <div className="text-xs font-medium" style={{ color: 'var(--text-3)' }}>
                      {devs.filter(d => d.role === role).length} developers with this role
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          loading ? (
            <div className="p-6 text-sm" style={{ color: 'var(--text-3)' }}>Loading…</div>
          ) : displayed.length === 0 ? (
            <div className="p-6 text-sm text-center" style={{ color: 'var(--text-3)' }}>No developers found for this filter.</div>
          ) : (
            <div className="overflow-x-auto">
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border)' }}>
                    <th className="px-4 py-3 text-left text-xs font-semibold sticky left-0" style={{ color: 'var(--text-3)', letterSpacing: '0.5px', textTransform: 'uppercase', background: 'var(--bg-secondary)', minWidth: 220 }}>Developer</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold" style={{ color: 'var(--text-3)', letterSpacing: '0.5px', textTransform: 'uppercase', minWidth: 110 }}>Role Preset</th>
                    {PERMISSIONS.map(p => (
                      <th key={p.key} className="px-2 py-3 text-center" style={{ minWidth: 80 }}>
                        <div className="flex flex-col items-center gap-1">
                          <span style={{ fontSize: 14 }}>{p.icon}</span>
                          <span className="text-xs font-semibold" style={{ color: 'var(--text-3)', letterSpacing: '0.3px', textTransform: 'uppercase', fontSize: 9, whiteSpace: 'nowrap' }}>{p.label}</span>
                        </div>
                      </th>
                    ))}
                    <th className="px-3 py-3 text-center text-xs font-semibold" style={{ color: 'var(--text-3)', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Save</th>
                  </tr>
                </thead>
                <tbody>
                  {displayed.map((dev, i) => {
                    const role = ROLES[dev.role] || ROLES.viewer;
                    const devPerms = perms[dev._id] || {};
                    const isDirty = dirty[dev._id];
                    return (
                      <tr key={dev._id} style={{ borderBottom: '1px solid var(--border)', background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,.01)' }}
                        className="hover:bg-white/[0.02]">
                        {/* Developer */}
                        <td className="px-4 py-3 sticky left-0" style={{ background: i % 2 === 0 ? 'var(--bg-primary)' : 'rgba(255,255,255,.01)' }}>
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0"
                              style={{ background: dev.avatarColor || '#3b82f6' }}>
                              {initials(dev.name)}
                            </div>
                            <div>
                              <div className="font-medium text-xs">{dev.name}</div>
                              <div className="text-xs" style={{ color: 'var(--text-3)', fontSize: 10 }}>{dev.email}</div>
                            </div>
                          </div>
                        </td>
                        {/* Role preset */}
                        <td className="px-3 py-3">
                          <select value={dev.role}
                            onChange={e => applyRole(dev._id, e.target.value)}
                            style={{ padding: '3px 6px', borderRadius: 5, fontSize: 11, fontWeight: 700, background: role.bg, color: role.color, border: `1px solid ${role.color}40`, cursor: 'pointer', outline: 'none' }}>
                            {Object.entries(ROLES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                          </select>
                        </td>
                        {/* Permission toggles */}
                        {PERMISSIONS.map(p => {
                          const has = !!devPerms[p.key];
                          return (
                            <td key={p.key} className="px-2 py-3 text-center">
                              <button onClick={() => toggle(dev._id, p.key)}
                                title={p.desc}
                                style={{
                                  width: 26, height: 26, borderRadius: 6, border: 'none', cursor: 'pointer',
                                  background: has ? 'rgba(34,197,94,.2)' : 'rgba(100,116,139,.15)',
                                  color: has ? '#22c55e' : '#475569',
                                  lineHeight: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                  transition: 'all .15s',
                                }}>
                                {has ? <Check size={13} /> : <X size={13} />}
                              </button>
                            </td>
                          );
                        })}
                        {/* Save */}
                        <td className="px-3 py-3 text-center">
                          {saved === dev._id ? (
                            <span style={{ color: '#22c55e', fontSize: 11, fontWeight: 600 }}>✓ Saved</span>
                          ) : (
                            <button onClick={() => savePerms(dev._id)} disabled={!isDirty || saving === dev._id}
                              className="flex items-center gap-1 px-3 py-1 rounded text-xs font-medium mx-auto"
                              style={{
                                background: isDirty ? 'rgba(59,130,246,.2)' : 'var(--bg-tertiary)',
                                color:      isDirty ? '#60a5fa' : 'var(--text-3)',
                                border:     `1px solid ${isDirty ? 'rgba(59,130,246,.4)' : 'var(--border)'}`,
                                cursor: isDirty ? 'pointer' : 'default',
                                opacity: saving === dev._id ? .5 : 1,
                              }}>
                              <Save size={11} /> {saving === dev._id ? '…' : 'Save'}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Legend */}
              <div className="flex items-center gap-4 px-6 py-4 text-xs" style={{ color: 'var(--text-3)', borderTop: '1px solid var(--border)' }}>
                <span className="flex items-center gap-1"><Check size={11} style={{ color: '#22c55e' }} /> Permission granted</span>
                <span className="flex items-center gap-1"><X size={11} style={{ color: '#475569' }} /> Permission denied</span>
                <span className="flex items-center gap-1"><Info size={11} /> Click any cell to toggle · Use "Role Preset" to reset to defaults · Hit Save to apply changes</span>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
}
